/**
 * Task sidebar badge store.
 * Written by `<TaskBadgeSubscriber />`; read by `tasksMenu`.
 *
 * `unseenAssigned` / `overdueAssigned` are kept live by SignalR `BadgeDelta`
 * events. `loopedInActive` / `myTasksActive` / `upcoming` have no server-side
 * events - they are refreshed only by the REST baseline on mount / reconnect.
 */

import { create } from 'zustand';

interface TaskBadgesState {
  unseenAssigned: number;
  overdueAssigned: number;
  loopedInActive: number;
  myTasksActive: number;
  /** Scheduled tasks waiting for their StartDate - the Upcoming page's total. */
  upcoming: number;
  setFromMetrics: (m: TaskBadgeMetrics) => void;
  applyDelta: (m: TaskBadgeDeltas) => void;
  reset: () => void;
}

/** Fields a baseline refresh or server snapshot may carry. */
export type TaskBadgeMetrics = Partial<
  Pick<
    TaskBadgesState,
    'unseenAssigned' | 'overdueAssigned' | 'loopedInActive' | 'myTasksActive' | 'upcoming'
  >
>;

/** Fields the server pushes as live deltas. */
export type TaskBadgeDeltas = Partial<Pick<TaskBadgesState, 'unseenAssigned' | 'overdueAssigned'>>;

const EMPTY = {
  unseenAssigned: 0,
  overdueAssigned: 0,
  loopedInActive: 0,
  myTasksActive: 0,
  upcoming: 0,
};

// Counters clamp at zero so a stray -1 delta that arrives before the
// baseline can't push the badge into negative numbers.
const clamp = (n: number) => (n < 0 ? 0 : n);

export const useTaskBadgesStore = create<TaskBadgesState>((set) => ({
  ...EMPTY,
  // Merge, don't replace: an empty snapshot (the server has no badge snapshot
  // populated yet) must not zero counts the baseline already filled in.
  setFromMetrics: (m) =>
    set((state) => ({
      unseenAssigned: clamp(m.unseenAssigned ?? state.unseenAssigned),
      overdueAssigned: clamp(m.overdueAssigned ?? state.overdueAssigned),
      loopedInActive: clamp(m.loopedInActive ?? state.loopedInActive),
      myTasksActive: clamp(m.myTasksActive ?? state.myTasksActive),
      upcoming: clamp(m.upcoming ?? state.upcoming),
    })),
  applyDelta: (m) =>
    set((state) => ({
      unseenAssigned: clamp(state.unseenAssigned + (m.unseenAssigned ?? 0)),
      overdueAssigned: clamp(state.overdueAssigned + (m.overdueAssigned ?? 0)),
    })),
  reset: () => set(EMPTY),
}));
